import { FluentProvider, makeStyles, Spinner } from '@fluentui/react-components';
import { createHashHistory, createRouter, RouterProvider } from '@tanstack/react-router';
import { CompactEncrypt } from 'jose';
import { StrictMode, Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import { fetchLaunchContext } from './auth/launchContext';
import { registerLoginRoute } from './auth/login';
import { routeTree } from './routeTree.gen';
import { userSessionStorageKey } from './store/userSessionStorageKey';
import { ThemeProvider, useThemeContext } from './theme/ThemeContext';
import './main.css';
import './styles/bpmn-theme-light.css';
import './styles/bpmn-theme-dark.css';
import { RootErrorBoundary } from './root-error-boundary';

// main.tsx, ganz oben
globalThis.addEventListener('error', (e) => {
    console.error('[GLOBAL ERROR]', {
        message: e.message,
        filename: e.filename,
        line: e.lineno,
        col: e.colno,
        stack: e.error?.stack,
        component: (e.error as any)?.componentStack,
    });
});

globalThis.addEventListener('unhandledrejection', (e) => {
    console.error('[UNHANDLED REJECTION]', {
        reason: e.reason,
        stack: e.reason?.stack,
    });
});

if (import.meta.env.LOGIN_ENABLED) {
    registerLoginRoute(routeTree);
}

const useStyles = makeStyles({
    provider: {
        display: 'grid',
        gridTemplateRows: 'auto 1fr auto',
        height: '100vh',
    },
    spinner: {
        justifyContent: 'unset',
        margin: '5px',
    },
});

// Pending-Komponente außerhalb, als eigene Komponente — dort darf useStyles() laufen
const PendingSpinner = () => {
    const styles = useStyles();
    return <Spinner className={styles.spinner} label="Ladevorgang..." labelPosition="below" />;
};

// Router kann synchron auf Modulebene erstellt werden — hier dürfen keine Hooks laufen
const router = createRouter({
    defaultPendingComponent: PendingSpinner,
    defaultGcTime: 0,
    defaultStaleTime: Infinity,
    defaultStructuralSharing: true,
    defaultPendingMs: 0,
    defaultPendingMinMs: 250,
    history: createHashHistory(),
    routeTree,
});

declare module '@tanstack/react-router' {
    interface HistoryState {
        xml?: string;
    }
    interface Register {
        router: typeof router;
    }
}

// App ist eine normale React-Funktionskomponente — hier dürfen Hooks laufen
const App = () => {
    const styles = useStyles();
    const { theme } = useThemeContext();
    console.log();
    return (
        <FluentProvider className={styles.provider} theme={theme} applyStylesToPortals={false}>
            <Suspense fallback={<Spinner />}>
                <RouterProvider router={router} />
            </Suspense>
        </FluentProvider>
    );
};

/**
 * Launch-Code vor dem Router-Start verarbeiten.
 * Holt den User via fetchLaunchContext, verschlüsselt ihn in sessionStorage
 * und bereinigt die URL. So sieht der Router von Anfang an eine saubere URL.
 */
async function processLaunchCode(): Promise<void> {
    const url = new URL(location.href);
    const launchCode = url.searchParams.get('launchCode');
    if (!launchCode) return;

    sessionStorage.removeItem(userSessionStorageKey);

    try {
        const user = await fetchLaunchContext(launchCode);
        if (user) {
            const encUser = await new CompactEncrypt(new TextEncoder().encode(JSON.stringify(user)))
                .setProtectedHeader({ alg: 'PBES2-HS256+A128KW', enc: 'A128GCM' })
                .encrypt(new TextEncoder().encode(userSessionStorageKey + location.hostname));
            sessionStorage.setItem(userSessionStorageKey, encUser);
        }
    } catch (err) {
        console.error('Launch context could not be processed', err);
    }

    // URL bereinigen: launchCode entfernen UND sicherstellen, dass ein Hash existiert
    url.searchParams.delete('launchCode');
    if (!url.hash) {
        url.hash = '#/';
    }
    history.replaceState(null, '', url.toString());
}

async function bootstrap(): Promise<void> {
    await processLaunchCode();

    createRoot(document.getElementById('root')!).render(
        <StrictMode>
            <RootErrorBoundary>
                <ThemeProvider>
                    <App />
                </ThemeProvider>
            </RootErrorBoundary>
        </StrictMode>
    );
}

void bootstrap();
